<template>
  <div class="hero-section mx-auto">
    <div class="hero-content mx-auto text-center">
      <!-- Titel über der gesamten Hero-Sektion -->
      <h3 class="portrait-title fade-in-title">{{ $t('hero.title', { spiritual: $t('hero.spiritual') }) }}</h3>

      <!-- Bild mit Qualifikationen - Animiert -->
      <div class="hero-with-pills-container mt-0 mb-1 md:mt-0 md:mb-2 flex justify-center items-center">
        <!-- Portrait Bild - Zentriert am Anfang -->
        <div class="hero-image-wrapper my-4">
          <img
            src="https://i.ibb.co/B2mNBmr4/dawid-personalcoding.png"
            :alt="$t('hero.imageAlt')"
            class="portrait-img"
          >
        </div>

        <!-- Pills - Am Anfang übereinander auf dem Bild, dann nach rechts -->
        <div class="pills-container hidden md:flex">
          <div class="proof-pill pill-animated pill-1">{{ $t('hero.proof1') }}</div>
          <div class="proof-pill pill-animated pill-2">{{ $t('hero.proof2') }}</div>
          <div class="proof-pill pill-animated pill-3">{{ $t('hero.proof3') }}</div>
        </div>
      </div>

      <!-- Klare Zielgruppe + Angebot mit Keyword-Highlighting -->
      <h1 class="main-headline text-[2.2rem] md:text-[3.5rem] leading-[1.2] md:leading-[1.1]">
        <span class="keyword-highlight">{{ $t('hero.clientAcquisition') }}</span> {{ $t('hero.headline') }} <span class="keyword-highlight">{{ $t('hero.coachingSuccess') }}</span><br>
        {{ $t('hero.headline2') }} <span class="keyword-highlight">{{ $t('hero.noTechStress') }}</span>
      </h1>
      <h2 class="sub-headline !text-[1.4rem] !md:text-[0.1rem] leading-[1.2] md:leading-[1.4]" v-html="subheadlineHtml">
      </h2>

      <!-- Spezialisierung + Zielgruppen-Tags -->
      <p class="audience-label mt-20 mb-2 text-sm md:text-base font-bold">{{ $t('hero.specializationLabel') }}</p>
      <div class="specialization-badges">
        <!-- Spirituelle Systeme: Desktop 1 Reihe, Mobile 2x2 -->
        <div class="spiritual-badges">
          <span class="tag tag-astro text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.astrology') }}</span>
          <span class="tag tag-hd text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.humanDesign') }}</span>
          <span class="tag tag-num text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.numerology') }}</span>
          <span class="tag tag-dm text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.destinyMatrix') }}</span>
        </div>
        <!-- Wellness Praktiken: Desktop 1 Reihe, Mobile 1 Reihe -->
        <div class="wellness-badges">
          <span class="tag tag-med text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.meditation') }}</span>
          <span class="tag tag-bw text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.breathwork') }}</span>
          <span class="tag tag-yoga text-xs md:text-sm px-3 py-2">{{ $t('hero.tags.yoga') }}</span>
        </div>
      </div>

      <!-- Mini Header -->
      <p class="intro-header text-[2rem] md:text-[2rem] font-bold">{{ $t('hero.introHeader') }}</p>

      <!-- Vorstellung als Fließtext -->
      <p class="intro-text mb-12 text-[1rem] md:text-[1.05rem] leading-relaxed max-w-[600px] mx-auto" v-html="introTextHtml">
      </p>

      <!-- Mission Statement -->
      <div class="text-[1.1rem] md:text-[1.2rem] font-bold pt-5 max-w-[300px] mx-auto">
        {{ $t('hero.mission') }}
      </div>

      <!-- Haupt-CTA -->
      <div class="main-cta mt-8">
        <div class="cta-buttons">
          <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener"
            class="cta-button primary-cta inline-block w-full md:w-auto text-center rounded-xl px-6 md:px-10 py-3 md:py-4 text-base md:text-lg shadow-[0_8px_25px_rgba(102,126,234,0.4)]">
            {{ $t('hero.ctaButton') }}
          </a>
          <a href="https://www.instagram.com/personalcoding.de" target="_blank" rel="noopener"
            class="cta-button instagram-cta inline-block w-full md:w-auto text-center rounded-xl px-6 md:px-10 py-3 md:py-4 text-base md:text-lg">
            {{ $t('hero.instagramButton') }}
          </a>
        </div>
        <p class="cta-subtext mt-3 text-slate-400 text-sm">{{ $t('hero.ctaSubtext') }}</p>
      </div>

    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'HeroSection',
  setup() {
    const { t } = useI18n()
    
    const introTextHtml = computed(() => {
      const dawidName = t('hero.dawid')
      const introText = t('hero.introText', { dawid: dawidName })
      return introText.replace(dawidName, `<strong class="text-white">${dawidName}</strong>`)
    })
    
    const subheadlineHtml = computed(() => {
      const spirituality = `<strong>${t('hero.spirituality')}</strong>`
      const technology = `<strong>${t('hero.technology')}</strong>`
      const collectiveConsciousness = `<strong>${t('hero.collectiveConsciousness')}</strong>`
      return t('hero.subheadline', { spirituality, technology, collectiveConsciousness })
    })
    
    return {
      introTextHtml,
      subheadlineHtml
    }
  }
}
</script>

<style scoped>
/* Hero Section Theme Support */
.hero-section {
  background: var(--gradient-secondary);
  color: var(--text-primary);
}

.intro-text {
  color: var(--text-secondary);
  opacity: 0;
  animation: fadeInUp 1.5s ease-out 3.7s forwards;
}

.intro-header {
  color: var(--text-primary);
}

.mission-statement {
  color: var(--text-primary);
}

.proof-pill {
  background: transparent;
  backdrop-filter: none;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.tag {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.tag-astro {
  background: var(--glass-bg);
  border-color: rgba(138, 43, 226, 0.3);
}

.tag-hd {
  background: var(--glass-bg);
  border-color: rgba(255, 165, 0, 0.3);
}

.tag-num {
  background: var(--glass-bg);
  border-color: rgba(0, 191, 255, 0.3);
}

.tag-dm {
  background: var(--glass-bg);
  border-color: rgba(255, 20, 147, 0.3);
}

.tag-med {
  background: var(--glass-bg);
  border-color: rgba(50, 205, 50, 0.3);
}

.tag-bw {
  background: var(--glass-bg);
  border-color: rgba(135, 206, 235, 0.3);
}

.tag-yoga {
  background: var(--glass-bg);
  border-color: rgba(255, 99, 71, 0.3);
}
</style>

<style scoped>
.hero-section {
  padding: 120px 20px 80px;
  padding-top: calc(120px + env(safe-area-inset-top));
  max-width: 1200px;
  margin: 0 auto;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

/* Social Proof Badge */
.social-proof-badge {
  background: rgba(0, 255, 179, 0.1);
  border: 1px solid rgba(0, 255, 179, 0.3);
  border-radius: 25px;
  padding: 8px 20px;
  margin-bottom: 30px;
  display: inline-block;
}

.proof-text {
  color: var(--accent-green, #00ffb3);
  font-size: 0.9rem;
  font-weight: 600;
}

/* Additional proof badges */
.proof-extras {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin: 8px 0 22px;
}

.proof-pill {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  background: transparent;
  border: 1px solid var(--border-color);
  backdrop-filter: none;
  box-shadow: none;
}

/* Vertical proof list */
.proof-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 24px;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: center;
}

.proof-list li {
  color: var(--text-light); /* leicht grünlich für Konsistenz */
  font-weight: 600;
  font-size: 0.95rem;
  display: inline-block;
  text-align: center;
  padding: 8px 14px;
  border-radius: 9999px;
  background: transparent;
  border: 1px solid var(--border-color);
}

/* Hero Container mit Animation */
.hero-with-pills-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0;
}

        /* Portrait Bild - Zentriert bleiben */
        .hero-image-wrapper {
          position: relative;
          z-index: 2;
          animation: fadeInScale 1.5s cubic-bezier(0.4, 0, 0.2, 1) 1.0s forwards;
        }

        /* Titel über der Hero-Sektion */
        .portrait-title {
          color: white;
          font-size: 2.0rem;
          font-weight: 700;
          text-align: center;
          white-space: nowrap;
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 0.0s forwards;
          margin-bottom: 10px;
          line-height: 1.1;
        }

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.portrait-img {
  width: 250px;
  height: auto;
  border-radius: 50%;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
}

/* Pills Container - Absolute positioning für Animation */
.pills-container {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  pointer-events: none;
}

/* Pills Styling */
.proof-pill {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.75rem;
  padding: 8px 16px;
  border-radius: 9999px;
  background: transparent;
  backdrop-filter: none;
  border: 1px solid var(--border-color);
  white-space: nowrap;
  box-shadow: none;
  text-align: left;
  display: inline-block;
  opacity: 0;
  pointer-events: all;
}

        /* Pills Animation - Starten übereinander, dann nach rechts verschieben */
        .pill-animated {
          animation: pillSlideOut 1.0s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        .pill-1 {
          animation-delay: 1.5s;
        }

        .pill-2 {
          animation-delay: 1.7s;
        }

        .pill-3 {
          animation-delay: 1.9s;
        }

@keyframes pillSlideOut {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translate(0, calc(var(--pill-offset) * 1px)) scale(1);
  }
}

/* Individuelle Positionen nach Animation */
.pill-1 {
  --pill-offset: -60;
}

.pill-2 {
  --pill-offset: 0;
}

.pill-3 {
  --pill-offset: 60;
}

/* Desktop: Pills rechts neben dem Bild */
@media (min-width: 1024px) {
  .pills-container {
    position: absolute;
    left: calc(50% + 150px);
    top: 50%;
    transform: translate(0, -50%);
    gap: 20px;
  }
  
  .proof-pill {
    font-size: 0.85rem;
    padding: 10px 20px;
  }
  
  @keyframes pillSlideOut {
    0% {
      opacity: 0;
      transform: translate(-200px, 0) scale(0.8);
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
  }
}

/* Pills overlay - positioned from top of image, centered (Mobile & Desktop) */
.pills-overlay {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: block;
  width: 100%;
  z-index: 10;
  padding-top: 10px;
}

.pills-overlay .proof-pill {
  position: absolute;
}

/* Treppe von oben nach unten, diagonal - Mobile (links-oben nach rechts-unten) */
.pill-1 {
  top: 22px;
  left: 165px;
}

.pill-2 {
  top: 62px;
  left: 165px;
}

.pill-3 {
  top: 102px;
  left: 165px;
}

/* Desktop: größere Abstände */
@media (min-width: 1024px) {
  .pill-1 {
    top: 20px;
    left: 165px;
  }

  .pill-2 {
    top: 60px;
    left: 165px;
  }

  .pill-3 {
    top: 100px;
    left: 165px;
  }
}

/* oneliner bleibt auch mobil ohne Umbruch */

        /* Headlines */
        .main-headline {
          font-size: 3.5rem;
          font-weight: 700;
          color: var(--text-primary) !important; /* Grundfarbe weiß */
          margin-bottom: 20px;
          line-height: 1.1;
          word-break: break-word;
          overflow-wrap: anywhere;
          hyphens: auto;
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 2.2s forwards;
          will-change: opacity, transform;
        }

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.keyword-highlight {
  background: var(--gradient-blue-purple);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  position: relative;
  display: inline-block;
  animation: gradientShift 3s ease infinite;
  background-size: 200% 200%;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

        .sub-headline {
          color: var(--text-secondary);
          margin-bottom: 40px;
          word-break: break-word;
          overflow-wrap: break-word;
          hyphens: none;
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 3.0s forwards;
        }

        .mission-line {
          font-size: 1.1rem;
          color: var(--text-primary) !important;
          font-weight: bold !important;
          margin: -20px 0 30px;
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 3.9s forwards;
        }

.bridge-line {
  font-size: 1.05rem;
  color: var(--text-secondary);
  margin: -10px 0 28px;
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;
}

/* Value Points */
.value-points {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.value-point {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.value-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.value-text {
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
}

        /* Spezialisierungs-Badges */
        .specialization-badges {
          display: flex;
          flex-direction: column;
          gap: 20px; /* mehr vertikaler Abstand zwischen den Gruppen */
          margin: 0 auto 24px; /* zentriert */
          max-width: 600px;
          text-align: center; /* Text zentriert */
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 4.1s forwards;
        }
 
/* Spirituelle Badges: Mobile 2x2, Desktop 1 Reihe */
.spiritual-badges {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px; /* kompakter horizontal, ausreichend vertikal */
  justify-items: center; /* zentriert auf Mobile */
}
 
@media (min-width: 768px) {
  .spiritual-badges {
    display: flex;
    justify-content: center; /* zentriert auf Desktop */
    gap: 12px;
  }
}
 
/* Wellness Badges: Mobile und Desktop 1 Reihe */
.wellness-badges {
  display: flex;
  justify-content: center; /* zentriert */
  gap: 12px;
  flex-wrap: wrap;
}
 
 .spiritual-badges .tag,
 .wellness-badges .tag {
   min-width: 120px;
   text-align: center;
   flex: 0 0 auto;
 }
 
 /* Zielgruppen-Tags */
 .audience-tags {
   display: flex;
   flex-wrap: wrap;
   gap: 10px;
   justify-content: center;
   margin: 18px auto 30px;
   max-width: 760px;
 }
 
 .audience-tags--primary { margin-bottom: 16px; }
 .audience-tags--secondary { margin-top: 6px; }

        .audience-label {
          color: var(--text-primary) !important;
          font-weight: bold !important;
          font-size: 1rem;
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 4.1s forwards;
        }

        .intro-header {
          color: var(--text-primary) !important;
          font-weight: bold !important;
          margin-top: 5em;
          opacity: 0;
          animation: fadeInUp 1.5s ease-out 3.5s forwards;
        }

.tag {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--accent-green, #00ffb3);
  background: rgba(0, 255, 179, 0.10);
  border: 1px solid rgba(0, 255, 179, 0.30);
  box-shadow: 0 0 0 1px rgba(0, 255, 179, 0.08) inset;
}

.tag-astro,
.tag-hd,
.tag-num,
.tag-dm,
.tag-med,
.tag-bw,
.tag-yoga { background: rgba(0, 255, 179, 0.10); color: var(--accent-green); }

/* Intro Name */
.intro-name {
  margin-top: 8px;
  color: var(--text-secondary);
  font-size: 1rem;
}

.credentials {
  margin-top: 4px;
  color: var(--text-muted);
  font-size: 0.95rem;
}

.about-cred {
  margin-top: 6px;
  color: var(--text-secondary);
  font-size: 1rem;
}

/* Main CTA */
.main-cta {
  margin-top: 40px;
}

        .primary-cta {
          background: var(--gradient-blue-purple);
          color: var(--text-primary);
          padding: 18px 40px;
          border-radius: 12px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.1rem;
          display: inline-block;
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
          border: none;
          cursor: pointer;
        }

.primary-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.6);
  background: var(--gradient-purple-blue);
}

.instagram-cta {
  background: var(--gradient-instagram);
  color: var(--text-primary);
  padding: 18px 40px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  display: inline-block;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(188, 24, 136, 0.35);
  border: none;
  cursor: pointer;
}

.instagram-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(188, 24, 136, 0.5);
}

.cta-subtext {
  margin-top: 12px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.hero-image {
  max-width: 320px;
  margin: 80px auto 30px;
  display: flex;
  justify-content: center;
}

.portrait {
  width: 100%;
  height: auto;
  border-radius: 20px;
}

.portrait-img {
  width: 100%;
  max-width: 250px;
  height: auto;
  border-radius: 20px;
  display: block;
}

.hero-image-wrapper {
  max-width: 250px;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
}

h2 {
  font-size: 3.5rem;
  margin-bottom: 20px;
}

.highlight-purple {
  color: var(--accent-purple);
}

.highlight-green {
  color: var(--accent-green, #00ffb3);
}

.highlight-gradient {
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
}

.wave { display: inline-block; }

.typing-container {
  display: inline-block;
  min-width: 2ch;
}

.typing { border-right: 2px solid transparent; }

.hero-intro {
  font-size: 1.2rem;
  line-height: 1.6;
  margin: 20px 0;
  color: rgba(255, 255, 255, 0.9);
}

.frequency-integration {
  margin: 40px 0;
  text-align: center;
  overflow: visible;
}

.frequency-title {
  font-size: 2.5rem;
  margin-bottom: 15px;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
}

.cta-buttons {
  margin: 30px 0;
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
}

.cta-button {
  display: inline-block;
  color: white;
  padding: 15px 25px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.primary-cta {
  background: var(--gradient-purple-pink);
}

.secondary-cta {
  background: var(--gradient-green-blue);
}

.tertiary-cta {
  background: var(--gradient-orange-red);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.social-follow {
  margin-top: 30px;
  text-align: center;
}

.instagram-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--gradient-instagram);
  color: white;
  padding: 10px 20px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 500;
  margin: 10px auto 0 auto;
  width: fit-content;
}

@media (max-width: 768px) {
  .hero-section {
    padding: 100px 20px 60px;
    padding-top: calc(100px + env(safe-area-inset-top));
    text-align: center;
  }

  .hero-image {
    max-width: 280px;
    margin: 60px auto 20px;
  }

  .main-headline {
    font-size: 2.5rem;
  }

  .portrait-title {
    font-size: 1.5rem;
  }

  .audience-tags {
    gap: 8px;
    margin: 14px auto 24px;
  }

  .audience-label {
    font-size: 0.95rem;
  }

  .tag {
    font-size: 0.85rem;
    padding: 6px 10px;
  }

  .value-points {
  gap: 12px;
  }

  .value-point {
    padding: 10px 16px;
  }

  .value-text {
    font-size: 0.9rem;
  }

  .primary-cta {
    padding: 16px 32px;
    font-size: 1rem;
  }

  .instagram-cta {
    padding: 16px 32px;
    font-size: 1rem;
  }

  .social-proof-badge {
    padding: 6px 16px;
    margin-bottom: 20px;
  }

  .proof-text {
    font-size: 0.8rem;
  }
}

/* Extra Feinjustierung für sehr schmale Geräte (z.B. 430px Breite) */
@media (max-width: 430px) {
  .main-headline {
    font-size: 1.9rem;
    line-height: 1.2;
  }

  .portrait-title {
    font-size: 1.5rem;
    line-height: 1.2;
    margin-bottom: 8px;
  }

  .primary-cta { padding: 12px 20px; font-size: 0.9rem; }
  .instagram-cta { padding: 12px 20px; font-size: 0.9rem; }
  .hero-image { max-width: 220px; }
  .audience-tags { margin: 12px auto 18px; gap: 8px; }
  .audience-tags--primary { margin-bottom: 14px; }
  .proof-list { gap: 6px; }
  .proof-list li { padding: 6px 10px; font-size: 0.85rem; }
  
  /* Force hide pills on mobile */
  .pills-container {
    display: none !important;
  }
}

@media (min-width: 768px) {
  .pills-container {
    display: flex !important;
  }
}
</style> 